// src/hooks/useDistContext.ts
import { useQuery, useMutation } from '@apollo/client';

// === GraphQL Запросы и мутации ===
import { GET_NOTES, GET_PAGE_CONTENT } from '../graphql/queries';
import { SET_PAGE_CONTENT, CLEAR_PAGE_CONTENT } from '../graphql/mutations';

// === Типы из общей системы ===
import { Note } from '../graphql/types'; // Убедитесь, что Note экспортируется

// Типы для контента
interface VideoData {
  videoUrl: string;
  title?: string;
  description?: string;
}

type ContentType = 'article' | 'video' | null;

export interface ActiveContent {
  type: ContentType;
  data: Note | VideoData | null;
}

export const useDistContext = (userRole: string | null) => {
  const canEdit = ['admin', 'superadmin'].includes(userRole || '');

  // Загружаем контент для страницы "dist"
  const { data: contentData, loading: contentLoading, refetch: refetchContent } = useQuery(GET_PAGE_CONTENT, {
    variables: { page: 'DIST' },
  });

  // Загружаем все заметки
  const { data: notesData, loading: notesLoading } = useQuery<{ notes: Note[] }>(GET_NOTES);

  // Мутации
  const [setPageContent] = useMutation(SET_PAGE_CONTENT);
  const [clearPageContent] = useMutation(CLEAR_PAGE_CONTENT);

  // Данные
  const pageContent = contentData?.pageContent || null;
  const notes = notesData?.notes || [];

  // Определяем активный контент
  const activeContent: ActiveContent = pageContent
  ? pageContent.contentType === 'video'
    ? {
        type: 'video',
        data: {
          videoUrl: pageContent.videoUrl,
          title: pageContent.title || undefined,
          description: pageContent.description || undefined,
        },
      }
    : {
        type: 'article',
        data: notes.find(n => n.id === pageContent.noteId) || null,
      }
  : { type: null, data: null };

  // Доступные статьи (не выбраны как контент)
  const availableNotes = notes.filter(note => note.id !== pageContent?.noteId);

  // Установить статью как контент на /dist
  const setAsDist = async (id: string): Promise<void> => {
    if (!canEdit) throw new Error('Недостаточно прав');

    try {
      await setPageContent({
        variables: {
          page: 'DIST',
          content: {
            contentType: 'article',
            noteId: id,
          },
        },
      });
    } catch (error) {
      console.error('Ошибка при установке статьи на /dist:', error);
      throw error;
    }

    await refetchContent();
  };

  // Удалить статью с /dist (очистка)
  const deleteArticle = async (): Promise<void> => {
    if (!canEdit) return;

    try {
      await clearPageContent({ variables: { page: 'DIST' } });
    } catch (error) {
      console.error('Ошибка при удалении статьи с /dist:', error);
    }

    await refetchContent();
  };

  // Установить видео на /dist
  const saveVideo = async (
    videoUrl: string,
    title?: string,
    description?: string
  ): Promise<void> => {
    if (!canEdit) throw new Error('Недостаточно прав');

    try {
      await setPageContent({
        variables: {
          page: 'DIST',
          content: {
            contentType: 'video',
            videoUrl,
            title,
            description,
          },
        },
      });
    } catch (error) {
      console.error('Ошибка при сохранении видео на /dist:', error);
      throw error;
    }

    await refetchContent();
  };

  // Очистить контент на /dist
  const clearVideo = async (): Promise<void> => {
    if (!canEdit) return;

    try {
      await clearPageContent({ variables: { page: 'DIST' } });
    } catch (error) {
      console.error('Ошибка при очистке контента /dist:', error);
    }

    await refetchContent();
  };

  return {
    activeContent,
    loading: contentLoading || notesLoading,
    canEdit,
    availableNotes,
    setAsDist,
    deleteArticle,
    saveVideo,
    clearVideo,
    refetch: refetchContent,
  };
};